import { useI18n } from '@ecom/i18n';
import {
  Badge,
  EmptyState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { usePaymentsOverview } from '../api';
import { PaymentBucketBadge } from '../components/PaymentBucketBadge';
import { PAYMENT_BUCKET_LABEL_KEY, type PaymentBucket } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { KpiCard } from '@/features/dashboard/components/KpiCard';
import { formatCatalogPrice } from '@/features/products/format-price';

const MONEY_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v18M17 7.5c0-1.8-2.2-3-5-3s-5 1.2-5 3 2.2 3 5 3 5 1.2 5 3-2.2 3-5 3-5-1.2-5-3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CLOCK_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CHECK_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CROSS_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('fr-TN', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

/**
 * "Paiements & COD" — aucune route marchande n'existe sur la table
 * `payments` réelle (voir `features/payments/types.ts`'s en-tête) ;
 * cette vue est donc explicitement dérivée du statut des commandes
 * (paiement à la livraison uniquement dans cet environnement), jamais
 * présentée comme une lecture directe de `payments`.
 */
export function PaymentsPage(): ReactElement {
  const { t } = useI18n();
  const { buckets, amountCollected, truncated, isPending, isError } = usePaymentsOverview();
  const [filter, setFilter] = useState<PaymentBucket | ''>('');

  // The ui-kit Select draws its own empty option (its placeholder): « Tous les statuts » is that option, not a second
  // '' entry the placeholder would shadow.
  const bucketFilterOptions = (
    Object.keys(PAYMENT_BUCKET_LABEL_KEY) as readonly PaymentBucket[]
  ).map((bucket) => ({
    value: bucket,
    label: t(PAYMENT_BUCKET_LABEL_KEY[bucket]),
  }));

  const pending = buckets.find((b) => b.bucket === 'pending');
  const confirmed = buckets.find((b) => b.bucket === 'confirmed');
  const failed = buckets.find((b) => b.bucket === 'failed');

  const visibleOrders =
    filter === ''
      ? buckets.flatMap((b) => b.orders.map((order) => ({ order, bucket: b.bucket })))
      : (buckets.find((b) => b.bucket === filter)?.orders ?? []).map((order) => ({
          order,
          bucket: filter,
        }));
  visibleOrders.sort((a, b) => b.order.createdAt.localeCompare(a.order.createdAt));

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="sk-page-title">{t('merchant.nav.payments')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.payments.subtitle')}</p>
      </div>

      {isPending ? (
        <TableSkeleton columns={4} />
      ) : isError ? (
        <EmptyState title={t('merchant.payments.loadError')} />
      ) : (
        <>
          {truncated ? (
            <div className="flex items-start gap-2 rounded-md border border-line bg-surface px-3 py-2.5">
              <Badge tone="info">{t('merchant.clients.truncatedNotice')}</Badge>
              <p className="text-sm text-inkFaint">{t('merchant.payments.truncatedDescription')}</p>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard
              label={t('merchant.payments.amountCollected')}
              value={formatCatalogPrice(amountCollected)}
              icon={MONEY_ICON}
              tone="accent"
              hint={t('merchant.payments.amountCollectedHint')}
            />
            <KpiCard
              label={t('merchant.payments.pendingKpi')}
              value={String(pending?.count ?? 0)}
              icon={CLOCK_ICON}
              tone="warn"
              hint={formatCatalogPrice(pending?.amount ?? 0)}
            />
            <KpiCard
              label={t('merchant.payments.confirmedKpi')}
              value={String(confirmed?.count ?? 0)}
              icon={CHECK_ICON}
              tone="good"
              hint={formatCatalogPrice(confirmed?.amount ?? 0)}
            />
            <KpiCard
              label={t('merchant.payments.failedKpi')}
              value={String(failed?.count ?? 0)}
              icon={CROSS_ICON}
              tone="critical"
              hint={formatCatalogPrice(failed?.amount ?? 0)}
            />
          </div>

          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-ink">
              {t('merchant.orders.title')}
            </h2>
            <Select
              aria-label={t('merchant.payments.filterAria')}
              options={bucketFilterOptions}
              placeholder={t('merchant.orders.filterAllStatuses')}
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as PaymentBucket | '');
              }}
            />
          </div>

          {visibleOrders.length === 0 ? (
            <EmptyState title={t('merchant.payments.emptyForStatus')} />
          ) : (
            <Table caption={t('merchant.nav.payments')}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.orders.table.colCustomer')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.payments.table.colPayment')}</TableHeaderCell>
                  <TableHeaderCell align="end">
                    {t('merchant.invoices.table.colAmount')}
                  </TableHeaderCell>
                  <TableHeaderCell>{t('merchant.orders.table.colDate')}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleOrders.map(({ order, bucket }) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <span className="font-mono text-sm text-ink">#{order.orderNumber}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-ink">{order.customerName}</span>
                    </TableCell>
                    <TableCell>
                      <PaymentBucketBadge bucket={bucket} />
                    </TableCell>
                    <TableCell align="end">
                      <span className="num text-sm text-ink">
                        {formatCatalogPrice(order.grandTotal)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-inkFaint">
                        {formatDateTime(order.createdAt)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
